/**
 * FRONT-776 drag-to-move: the two drag surfaces on the Files screen — the
 * "drop a file here to upload" dropzone (`routes/files.tsx`) and a folder
 * card as a drop target (`features/folders/folder-grid.tsx`) — must never
 * fire on each other's drag. Both existing handlers used to skip checking
 * `e.dataTransfer.types` entirely, so an in-page file-card drag lit up the
 * upload dropzone as if a desktop file were incoming (cosmetic, but wrong),
 * and the two surfaces were only accidentally non-colliding.
 *
 * `dataTransfer.types` is the one thing readable during `dragover` (unlike
 * `files`/`items`, which are empty until `drop`) — a real OS file drag
 * always includes `'Files'` in it; an in-page drag of our own custom
 * `FOLDER_DRAG_MIME` payload never does. So the same predicate, used in
 * opposite directions, guards both surfaces:
 *   - the upload dropzone ignores a drag where this is FALSE (no `'Files'` —
 *     not a real OS file drag);
 *   - a folder drop target ignores a drag where this is TRUE (`'Files'` IS
 *     present — a desktop file, not a card).
 */
export function isDesktopFileDrag(types: readonly string[] | undefined | null): boolean {
  if (!types) return false;
  return types.includes('Files');
}

/**
 * A window `dragleave` means the drag left the browser window only when it has no `relatedTarget` AND the
 * pointer is outside the viewport: Safari reports no related element on EVERY `dragleave`, so the first
 * condition alone would hide the overlay mid-drag.
 */
export function dragLeftWindow(e: { relatedTarget?: unknown; clientX?: number; clientY?: number }, viewport: { width: number; height: number }): boolean {
  if (e.relatedTarget != null) return false;
  const x = e.clientX ?? 0;
  const y = e.clientY ?? 0;
  return x <= 0 || y <= 0 || x >= viewport.width || y >= viewport.height;
}

/**
 * A distinct custom MIME type for the Files screen's card-drag payload — the
 * VALUE is a JSON-encoded array of `fileItemKey`-shaped strings (the carried
 * selection, see `resolveDragCarryKeys` below), deliberately NOT `text/plain`
 * so an in-page card drag can never be confused with any other text drag
 * (e.g. a user dragging selected text from elsewhere on the page). Nothing
 * actually decodes this VALUE on the drop side — every drop target reads the
 * carried keys off lifted React state instead (see `routes/files.tsx`'s
 * module docblock) — it exists so `dataTransfer.types` carries a type this
 * repo's own drag can be identified by.
 */
export const FOLDER_DRAG_MIME = 'application/x-eddy-file-key';

// ─────────────────────────────────────────────────────────────────────────
// Drag-ghost polish (Mattias, 2026-08-15): "smoother animation... especially
// on a folder... App character", plus a visible stack when several files are
// dragged at once. Design direction: motion inside the existing system, no
// new visual language — see routes/files.tsx's module docblock for the full
// brief and the technical constraint that shapes all of this (the HTML5
// drag image is a STATIC SNAPSHOT taken once at dragstart; CSS animation on
// it never plays, so the ghost's only lever is static composition — stack,
// tilt, shadow, badge — and every actual motion lives on the source cards,
// the folder targets and the drop confirmation instead).
// ─────────────────────────────────────────────────────────────────────────

/**
 * The multi-select drag rule (standard file-manager behaviour): grabbing a
 * card that's already part of the active multi-select carries the WHOLE
 * selection, grabbed-item-first (so the ghost's "top" chip and the transit
 * styling agree on which card is the primary one); grabbing a card that
 * ISN'T in the current selection carries just that one card, same as a
 * plain single-file drag. Pure so the rule — including the "not part of the
 * selection" case — is provable without a real drag (this repo's frontend
 * tests have no jsdom; see routes/files.tsx's module docblock).
 */
export function resolveDragCarryKeys(grabbedKey: string, selectedKeys: readonly string[]): string[] {
  if (!selectedKeys.includes(grabbedKey)) return [grabbedKey];
  return [grabbedKey, ...selectedKeys.filter((k) => k !== grabbedKey)];
}

/** Minimal shape the ghost needs off a carried file — just enough to render
 *  its chip, no `UnifiedFileItem` import here (kept decoupled; see
 *  `dragSourceForItem` in routes/files.tsx for the real mapping). */
export interface DragGhostSource {
  key: string;
  label: string;
  kind: 'image' | 'file';
}

export interface DragGhostModel {
  /** The grabbed item — always the top/foreground chip. */
  primary: DragGhostSource;
  /** Extra chips fanned behind the primary at slight opposing angles —
   *  capped at 2 regardless of how many are actually carried, so a big
   *  selection still reads as "a stack", not a pile (design: "the top chip
   *  with two cards fanned behind it"). 0 for a single-file drag — no stack
   *  at all. */
  fanCount: 0 | 1 | 2;
  /** Exact total carried, for the count badge. `null` for a single-file drag
   *  — the plain chip speaks for itself, no badge. */
  badgeCount: number | null;
}

/**
 * Turns the carried sources into the ghost's content model. `sources[0]` is
 * always the primary (grabbed) item — callers order this list themselves
 * (see `resolveDragCarryKeys`'s grabbed-first contract).
 */
export function buildDragGhostModel(sources: readonly DragGhostSource[]): DragGhostModel {
  if (sources.length === 0) {
    throw new Error('buildDragGhostModel requires at least one source');
  }
  const [primary] = sources;
  const extra = sources.length - 1;
  return {
    primary,
    fanCount: Math.min(extra, 2) as 0 | 1 | 2,
    badgeCount: extra > 0 ? sources.length : null,
  };
}

const ESCAPE_MAP: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;',
};

/** Escapes text for safe interpolation into the ghost's `innerHTML` — a file
 *  or document name is user-controlled, so this is the one thing standing
 *  between a cleverly-named file and script injection into the drag ghost. */
export function escapeHtml(value: string): string {
  return value.replace(/[&<>"']/g, (c) => ESCAPE_MAP[c] ?? c);
}

// Hand-drawn, deliberately minimal 16x16 glyphs (not lifted from the Phosphor
// bundle) — the ghost node is built as a plain HTML string, not React, so it
// can be mounted and read back synchronously inside the `dragstart` handler
// with no render-commit timing to worry about (see `mountDragGhost` below).
const FILE_ICON_SVG =
  '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.3" stroke-linejoin="round"><path d="M3.5 1.5h5.5l3 3v10h-8.5z"/><path d="M9 1.5v3h3"/></svg>';
const IMAGE_ICON_SVG =
  '<svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" stroke-width="1.3"><rect x="1.5" y="2.5" width="13" height="11" rx="1.5"/><circle cx="5.4" cy="6.3" r="1.1" fill="currentColor" stroke="none"/><path d="M2.2 11.8l3.6-3.6 2.3 2.3 3.4-3.4 2.5 3" stroke-linecap="round" stroke-linejoin="round"/></svg>';

function iconSvg(kind: DragGhostSource['kind']): string {
  return kind === 'image' ? IMAGE_ICON_SVG : FILE_ICON_SVG;
}

function chipHtml(
  source: DragGhostSource,
  opts: { z: number; rotateDeg: number; translateX: number; translateY: number; faded?: boolean },
): string {
  const { z, rotateDeg, translateX, translateY, faded } = opts;
  const style =
    `position:absolute;inset:0;transform:translate(${translateX}px,${translateY}px) rotate(${rotateDeg}deg);` +
    `z-index:${z};${faded ? 'opacity:0.85;' : ''}`;
  return (
    `<div style="${style}" class="flex items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1.5 shadow-lg text-foreground">` +
    `<span class="shrink-0 text-muted-foreground">${iconSvg(source.kind)}</span>` +
    `<span class="text-xs font-medium truncate" style="max-width:120px;">${escapeHtml(source.label)}</span>` +
    `</div>`
  );
}

/**
 * Pure HTML-string builder for the offscreen drag-image node — see the
 * section intro above for why the ghost has to be a static composition
 * rather than an animation. Kept as a pure string-in/string-out function so
 * the actual content decisions (a single chip vs. a fanned stack, the badge
 * text) are provable without touching a DOM, which this repo's frontend
 * tests have none of; `mountDragGhost` below is the thin, deliberately
 * un-unit-tested layer that actually paints this into the page.
 */
export function buildDragGhostHtml(model: DragGhostModel): string {
  const { primary, fanCount, badgeCount } = model;
  const fanChips: string[] = [];
  // Fanned siblings are drawn first (lower z-index) so the primary paints on
  // top of them, at slight OPPOSING angles so the stack doesn't just read as
  // one card offset twice in the same direction.
  if (fanCount >= 1) {
    fanChips.push(chipHtml(primary, { z: 1, rotateDeg: 7, translateX: 5, translateY: 3, faded: true }));
  }
  if (fanCount >= 2) {
    fanChips.push(chipHtml(primary, { z: 0, rotateDeg: -10, translateX: -4, translateY: 5, faded: true }));
  }
  const primaryChip = chipHtml(primary, { z: 2, rotateDeg: -2, translateX: 0, translateY: 0 });
  const badge =
    badgeCount != null
      ? `<span style="position:absolute;top:-7px;right:-7px;z-index:3;" class="flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-primary px-1 text-[length:var(--text-2xs)] font-semibold text-primary-foreground shadow">${badgeCount}</span>`
      : '';
  return `<div style="position:relative;width:170px;height:46px;">${fanChips.join('')}${primaryChip}${badge}</div>`;
}

/** Minimal shape `mountDragGhost` needs off `dataTransfer` — just enough to
 *  test callers against a plain object; `setDragImage` is what's missing
 *  from jsdom-less tests, so it's the one method real code depends on. */
export interface DragImageTransferLike {
  setDragImage: (image: Element, xOffset: number, yOffset: number) => void;
}

/**
 * Mounts the offscreen ghost node and arms it as the native drag image.
 * Deliberately impure (DOM mutation) and NOT unit tested — this repo's
 * frontend suite runs `renderToStaticMarkup` with no jsdom, so there is no
 * DOM to assert against here; `buildDragGhostHtml` above carries every
 * actual content decision and IS fully covered. `startFileCardDrag`
 * (routes/files.tsx) takes this as an injectable dependency precisely so its
 * OWN wiring — which item ends up in the model, in what order — stays
 * testable even though this function itself can't be.
 *
 * Must run synchronously inside the `dragstart` handler: Firefox requires
 * the drag-image element to already be in the DOM at the moment
 * `setDragImage` is called. The node is positioned `fixed` far off-viewport
 * (never `display:none`, which never paints and so never becomes a valid
 * drag image) so it's visible to the browser's snapshot but not to the user.
 * Callers MUST invoke the returned cleanup on `dragend` — which fires
 * unconditionally (drop on a folder, drop nowhere, or an Escape-cancelled
 * drag all trigger it) — or the offscreen node leaks.
 */
export function mountDragGhost(dataTransfer: DragImageTransferLike, model: DragGhostModel): () => void {
  const node = document.createElement('div');
  node.style.position = 'fixed';
  node.style.top = '-9999px';
  node.style.left = '-9999px';
  node.style.pointerEvents = 'none';
  node.innerHTML = buildDragGhostHtml(model);
  document.body.appendChild(node);
  dataTransfer.setDragImage(node, 18, 23);
  return () => {
    node.remove();
  };
}
